// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import api from './advisorApi';
import type {
  SignatoryStatusUpdateRequest,
  SignatoryStatusUpdateResult,
  ResolveFlagRequest,
  RePublishRequest,
  BulkPublishRequest,
  BulkOperationResult,
  SignatoryStatusSummaryDTO
} from '../types/signatoryStatus';

/**
 * Update the status of a policy signatory
 */
export const updateSignatoryStatus = async (
  profileId: string,
  policyId: string,
  signatoryId: string,
  request: SignatoryStatusUpdateRequest
): Promise<SignatoryStatusUpdateResult> => {
  const response = await api.put<SignatoryStatusUpdateResult>(
    `/profiles/${profileId}/policies/${policyId}/signatories/${signatoryId}/status`,
    request
  );
  return response.data;
};

/**
 * Resolve a flag on a policy signatory
 */
export const resolveFlag = async (
  profileId: string,
  policyId: string,
  signatoryId: string,
  request: ResolveFlagRequest
): Promise<SignatoryStatusUpdateResult> => {
  const response = await api.put<SignatoryStatusUpdateResult>(
    `/profiles/${profileId}/policies/${policyId}/signatories/${signatoryId}/resolve-flag`,
    request
  );
  return response.data;
};

/**
 * Request client review for a signatory (transitions to PENDING_REVIEW)
 */
export const requestReview = async (
  profileId: string,
  policyId: string,
  signatoryId: string,
  request: RePublishRequest
): Promise<SignatoryStatusUpdateResult> => {
  const response = await api.put<SignatoryStatusUpdateResult>(
    `/profiles/${profileId}/policies/${policyId}/signatories/${signatoryId}/request-review`,
    request
  );
  return response.data;
};

/**
 * Bulk activate all DRAFT signatories to PENDING_SIGNATURE for a profile
 */
export const bulkPublish = async (
  profileId: string,
  request: BulkPublishRequest
): Promise<BulkOperationResult> => {
  const response = await api.post<BulkOperationResult>(
    `/profiles/${profileId}/signatories/bulk-publish`,
    request
  );
  return response.data;
};

/**
 * Get signatory status summary counts for a profile
 */
export const getSignatoryStatusSummary = async (
  profileId: string
): Promise<SignatoryStatusSummaryDTO> => {
  const response = await api.get<SignatoryStatusSummaryDTO>(
    `/profiles/${profileId}/signatory-status-summary`
  );
  return response.data;
};

export default {
  updateSignatoryStatus,
  resolveFlag,
  requestReview,
  bulkPublish,
  getSignatoryStatusSummary,
};
